// Nueva vista para gestionar las asignaturas de los profesores 
import { useState, useEffect } from 'react';
import { BackButton } from './BackButton';
import { Teacher, CourseTemplate, Course, Pueblo } from '../types';
import { getTeachers, getCourseTemplates, getCourses, assignTeacherSubjects, getCenterConfig, getPueblos } from '../lib/db/index';
import { Listbox, Transition } from '@headlessui/react';
import { Check, ChevronsUpDown, PlusCircle } from 'lucide-react';

export function TeacherSubjects() {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [templates, setTemplates] = useState<CourseTemplate[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedTeacher, setSelectedTeacher] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllSubjects, setShowAllSubjects] = useState<{[courseId: string]: boolean}>({});
  const [isCra, setIsCra] = useState(false);
  const [pueblos, setPueblos] = useState<Pueblo[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      const [teachersData, templatesData, coursesData, centerConfigData, pueblosData] = await Promise.all([
        getTeachers(),
        getCourseTemplates(),
        getCourses(),
        getCenterConfig(),
        getPueblos(),
      ]);

      setTeachers(teachersData);
      setTemplates(templatesData);
      setCourses(coursesData);
      setIsCra(Boolean(centerConfigData?.isCra));
      setPueblos(pueblosData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  }

  const handleSaveSubjects = async (
    teacherId: string,
    courseId: string,
    subjects: Array<{name: string, type: 'individual' | 'group', puebloId?: string | null}>
  ) => {
    // Guardar estado anterior para revertir si falla
    const previousTeachers = teachers;

    // Actualizar UI inmediatamente (optimistic update)
    setTeachers(prevTeachers => prevTeachers.map(teacher => {
      if (teacher.id === teacherId) {
        return {
          ...teacher,
          courseSubjects: {
            ...teacher.courseSubjects,
            [courseId]: subjects
          }
        };
      }
      return teacher;
    }));

    try {
      await assignTeacherSubjects(teacherId, courseId, subjects);
    } catch (err) {
      console.error('Error saving subjects:', err);
      setTeachers(previousTeachers); // Revertir si falla
      setError('Error al guardar las asignaturas');
    }
  };

  const selectedTeacherData = teachers.find(t => t.id === selectedTeacher);

  return (
    <div className="py-6">
      <div className="px-4">
        <div className="mb-6">
          <BackButton />
        </div>
        
        <h1 className="mb-6 text-2xl font-semibold text-gray-900">
          Reparto de asignaturas
        </h1>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="w-12 h-12 rounded-full border-b-2 border-indigo-600 animate-spin"></div>
          </div>
        ) : error ? (
          <div className="p-4 text-center text-red-600">
            {error}
            <button
              onClick={loadData}
              className="ml-2 text-indigo-600 hover:text-indigo-800"
            >
              Reintentar
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Selector de Profesor mejorado */}
            <div className="max-w-xl">
              <Listbox value={selectedTeacher} onChange={setSelectedTeacher}>
                <div className="relative mt-1">
                  <Listbox.Button className="relative py-2 pr-10 pl-3 w-full text-left bg-white rounded-lg border border-gray-300 cursor-default focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-300 sm:text-sm">
                    <span className="block truncate">
                      {selectedTeacherData 
                        ? `${selectedTeacherData.firstName} ${selectedTeacherData.lastName} - ${selectedTeacherData.instrumentName}`
                        : 'Seleccione un profesor...'}
                    </span>
                    <span className="flex absolute inset-y-0 right-0 items-center pr-2 pointer-events-none">
                      <ChevronsUpDown className="w-5 h-5 text-gray-400" aria-hidden="true" />
                    </span>
                  </Listbox.Button>
                  <Transition
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="overflow-auto absolute py-1 mt-1 w-full max-h-60 text-base bg-white rounded-md ring-1 ring-black ring-opacity-5 shadow-lg focus:outline-none sm:text-sm">
                      {teachers.map((teacher) => (
                        <Listbox.Option
                          key={teacher.id}
                          value={teacher.id}
                          className={({ active }) => `
                            relative cursor-default select-none py-2 pl-10 pr-4
                            ${active ? 'bg-indigo-100 text-indigo-900' : 'text-gray-900'}
                          `}
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                {teacher.firstName} {teacher.lastName} - {teacher.instrumentName}
                              </span>
                              {selected && (
                                <span className="flex absolute inset-y-0 left-0 items-center pl-3 text-indigo-600">
                                  <Check className="w-5 h-5" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            </div>

            {/* Mostrar cursos y asignaturas del profesor seleccionado */}
            {selectedTeacherData && (
              <div className="mt-6 space-y-6">
                {(isCra ? courses.map(c => c.id) : selectedTeacherData.courseIds).map(courseId => {
                  const course = courses.find(c => c.id === courseId);
                  const template = templates.find(t => t.courseId === courseId) 
                    || templates.find(t => t.name === course?.name && t.levelId === course?.levelId);

                  const assignedSubjects = selectedTeacherData.courseSubjects?.[courseId] || [];
                  const showAll = showAllSubjects[courseId];

                  // Si no hay plantilla: en CRA ignorar silenciosamente, en modo normal mostrar error
                  if (!template) {
                    if (isCra) return null;
                    return (
                      <div key={courseId} className="p-6 bg-white rounded-lg border border-red-200">
                        <h3 className="mb-4 text-lg font-medium text-gray-900">
                          {course?.name}
                          <span className="ml-2 text-sm text-red-600">
                            (Error: No se encontró la plantilla para el nivel {course?.levelId})
                          </span>
                        </h3>
                      </div>
                    );
                  }

                  const groupSubjects = template.subjects.filter(s => s.type === 'group');
                  const individualSubjects = template.subjects.filter(s => s.type === 'individual');

                  return (
                    <div key={courseId} className="p-6 bg-white rounded-lg border shadow-sm">
                      <h3 className="mb-4 text-lg font-medium text-gray-900">
                        {course?.name}
                        <span className="ml-2 text-sm text-gray-500">
                          (Nivel: {course?.levelName || template.levelName})
                        </span>
                      </h3>

                      {isCra ? (
                        <div className="space-y-4">
                          {/* Asignaturas individuales */}
                          {individualSubjects.length > 0 && (
                            <div>
                              <p className="mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">Asignaturas individuales</p>
                              <div className="space-y-2">
                                {individualSubjects.map(subject => {
                                  const isAssigned = assignedSubjects.some(a => a.name === subject.name);
                                  return (
                                    <label key={subject.id} className="flex items-center">
                                      <input
                                        type="checkbox"
                                        checked={isAssigned}
                                        onChange={() => {
                                          const newSubjects = isAssigned
                                            ? assignedSubjects.filter(a => a.name !== subject.name)
                                            : [...assignedSubjects, { name: subject.name, type: subject.type }];
                                          handleSaveSubjects(selectedTeacherData.id, courseId, newSubjects);
                                        }}
                                        className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                                      />
                                      <span className="ml-2 text-sm text-gray-900">{subject.name}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Asignaturas grupales × pueblos */}
                          {groupSubjects.length > 0 && (
                            <div>
                              <p className="mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">Asignaturas grupales por pueblo</p>
                              {pueblos.length === 0 ? (
                                <p className="text-sm text-amber-600">Configura los pueblos en Ajustes → Pueblos del CRA primero.</p>
                              ) : (
                                <table className="w-full text-sm border-collapse">
                                  <thead>
                                    <tr>
                                      <th className="py-2 pr-4 text-left text-gray-700">Asignatura</th>
                                      {pueblos.map(p => (
                                        <th key={p.id} className="px-3 py-2 text-center text-gray-700">{p.name}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {groupSubjects.map(subject => (
                                      <tr key={subject.id} className="border-t">
                                        <td className="py-2 pr-4 font-medium text-gray-800">{subject.name}</td>
                                        {pueblos.map(pueblo => {
                                          const isAssigned = assignedSubjects.some(
                                            a => a.name === subject.name && a.puebloId === pueblo.id
                                          );
                                          // Buscar si OTRO profesor ya tiene esta combinación en este curso
                                          const otherTeacher = teachers.find(t =>
                                            t.id !== selectedTeacherData.id &&
                                            t.courseSubjects?.[courseId]?.some(
                                              a => a.name === subject.name && a.puebloId === pueblo.id
                                            )
                                          );
                                          const takenBy = otherTeacher
                                            ? `${otherTeacher.firstName} ${otherTeacher.lastName}`
                                            : null;

                                          return (
                                            <td key={pueblo.id} className="px-3 py-2 text-center">
                                              <div className="relative inline-flex justify-center items-center group">
                                                <input
                                                  type="checkbox"
                                                  checked={isAssigned || !!takenBy}
                                                  disabled={!!takenBy}
                                                  onChange={() => {
                                                    if (takenBy) return;
                                                    const newSubjects = isAssigned
                                                      ? assignedSubjects.filter(
                                                          a => !(a.name === subject.name && a.puebloId === pueblo.id)
                                                        )
                                                      : [...assignedSubjects, { name: subject.name, type: subject.type, puebloId: pueblo.id }];
                                                    handleSaveSubjects(selectedTeacherData.id, courseId, newSubjects);
                                                  }}
                                                  className={`w-4 h-4 rounded border-gray-300 focus:ring-0 ${
                                                    takenBy
                                                      ? 'accent-amber-500 cursor-not-allowed opacity-80'
                                                      : 'text-indigo-600 focus:ring-indigo-500 cursor-pointer'
                                                  }`}
                                                />
                                                {takenBy && (
                                                  <div className="absolute bottom-full left-1/2 z-10 px-2 py-1 mb-1 text-xs text-white whitespace-nowrap bg-gray-800 rounded shadow-lg opacity-0 -translate-x-1/2 pointer-events-none group-hover:opacity-100 transition-opacity">
                                                    {takenBy}
                                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                                                  </div>
                                                )}
                                              </div>
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          {/* Modo no-CRA: UI original */}
                          <div className="space-y-3">
                            {assignedSubjects.map(subject => (
                              <label key={`${subject.name}-${subject.puebloId}`} className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={true}
                                  onChange={() => {
                                    const newSubjects = assignedSubjects.filter(s => s.name !== subject.name);
                                    handleSaveSubjects(selectedTeacherData.id, courseId, newSubjects);
                                  }}
                                  className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                                />
                                <span className="ml-2 text-sm text-gray-900">{subject.name}</span>
                              </label>
                            ))}
                          </div>

                          <button
                            onClick={() => setShowAllSubjects(prev => ({...prev, [courseId]: !prev[courseId]}))}
                            className="inline-flex items-center mt-4 text-sm text-indigo-600 hover:text-indigo-800"
                          >
                            <PlusCircle className="mr-1 w-4 h-4" />
                            {showAll ? 'Ocultar asignaturas disponibles' : 'Añadir más asignaturas'}
                          </button>

                          {showAll && (
                            <div className="pl-4 mt-4 space-y-3 border-l-2 border-indigo-100">
                              {template?.subjects
                                .filter(subject => !assignedSubjects.some(s => s.name === subject.name))
                                .map(subject => (
                                  <label key={subject.id} className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={false}
                                      onChange={() => {
                                        const newSubjects = [
                                          ...assignedSubjects,
                                          { name: subject.name, type: subject.type }
                                        ];
                                        handleSaveSubjects(selectedTeacherData.id, courseId, newSubjects);
                                      }}
                                      className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-900">{subject.name}</span>
                                  </label>
                                ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
} 